/**
 * useEventReminders — fires browser Notification for CalendarEvent reminders.
 *
 * Scope: IN-APP only (tab must be open / PWA running). Background push (FCM) is a
 * separate effort. Handles both single and RECURRING events (today's occurrence via
 * expandRecurrence). Dedup is persisted in localStorage so a page reload near a
 * reminder time does not re-fire it.
 *
 * Triggers: a 30s interval AND on tab focus / visibility change (so returning to the
 * tab fires any reminder whose moment passed while hidden, within the stale window).
 */

import { useEffect, useRef } from "react";
import type { CalendarEvent } from "../types";
import { todayIso } from "../lib/date";
import { expandRecurrence } from "../lib/recurrence";
import { isPushSubscribed } from "../lib/push-reminders";

const TICK_MS = 30_000; // poll every 30 s
const WINDOW_MS = 90_000; // fire within 90 s after the reminder moment
const STALE_MS = 5 * 60_000; // ignore reminders older than 5 min (opened app late)
const STORE_KEY = "personal-life-os:reminders-fired:v1";

/** Parse "HH:MM" on an ISO date into a local Date; null if malformed. */
function parseEventDateTime(date: string, time: string): Date | null {
  if (!time || !/^\d{2}:\d{2}$/.test(time)) return null;
  const dt = new Date(`${date}T${time}:00`);
  return isNaN(dt.getTime()) ? null : dt;
}

/** Load fired keys for `today` only (prunes older days to keep storage small). */
function loadFired(today: string): Set<string> {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as { date?: string; keys?: string[] };
    return parsed.date === today && Array.isArray(parsed.keys) ? new Set(parsed.keys) : new Set();
  } catch {
    return new Set();
  }
}

function saveFired(today: string, keys: Set<string>) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify({ date: today, keys: [...keys] }));
  } catch {
    // private mode / quota — dedup falls back to the in-memory ref only
  }
}

export function useEventReminders(events: CalendarEvent[]): void {
  // In-memory mirror of fired keys (kept in sync with localStorage each tick).
  const firedRef = useRef<Set<string>>(new Set());
  // When this device has an active Web Push subscription, the SERVER delivers
  // reminders (even when closed) — so suppress this in-tab path to avoid a
  // duplicate notification when the app is open. Refreshed on mount + focus.
  const pushActiveRef = useRef(false);

  useEffect(() => {
    // Guard: Notification API absent (mobile webview, SSR)
    if (typeof window === "undefined" || !("Notification" in window)) return;

    let cancelled = false;

    const tick = () => {
      if (window.Notification.permission !== "granted") return;
      if (pushActiveRef.current) return; // background push handles delivery

      const today = todayIso();
      // Refresh from storage each tick so reloads / other tabs are respected.
      firedRef.current = loadFired(today);
      const now = Date.now();
      let changed = false;

      for (const event of events) {
        if (!event.reminders?.length) continue;
        // Does this event occur TODAY? (handles single + recurring + exdates)
        if (expandRecurrence(event, today, today).length === 0) continue;

        const eventDt = parseEventDateTime(today, event.time);
        if (!eventDt) continue;

        for (const offsetMin of event.reminders) {
          const fireTime = eventDt.getTime() - offsetMin * 60_000;
          const key = `${event.id}:${today}:${offsetMin}`;
          if (firedRef.current.has(key)) continue;

          // Too old → mark fired silently (avoids spam when opening app late).
          if (now - fireTime > STALE_MS) {
            firedRef.current.add(key);
            changed = true;
            continue;
          }

          if (now >= fireTime && now < fireTime + WINDOW_MS) {
            firedRef.current.add(key);
            changed = true;
            const label = offsetMin === 0 ? "Đúng giờ sự kiện" : `Còn ${offsetMin} phút`;
            new window.Notification(`🔔 ${event.title}`, {
              body: `${label} — ${event.time}`,
              tag: key, // browser dedups same tag
            });
          }
        }
      }

      if (changed) saveFired(today, firedRef.current);
    };

    // Refresh push-subscription status, then tick. Done on mount + focus so the
    // first tick already knows whether to defer to background push (avoids a
    // duplicate fired in the gap before the async check resolves).
    const refreshAndTick = () => {
      void isPushSubscribed()
        .then((active) => {
          if (cancelled) return;
          pushActiveRef.current = active;
          tick();
        })
        .catch(() => {
          if (!cancelled) tick();
        });
    };

    refreshAndTick();
    // Interval uses the cached status (cheap); focus/visibility re-checks it.
    const id = window.setInterval(tick, TICK_MS);
    const onFocus = () => refreshAndTick();
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshAndTick();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [events]);
}
